import { makeAutoObservable, runInAction } from 'mobx'
import { appOrigin, appWsUrl } from '@utils/appDomain'
import { normalizeLangCode } from '@utils/languageMeta'

type Translations = Record<string, any>

type PersistedState = {
  language: string
  translations: Translations
  etag?: string
  lastFetched?: number
}

const STORAGE_KEY = 'i18nState.api'
/** Язык по умолчанию — DEFAULT_LANG из .env (ru | en | ua | kz), пока пользователь не выбрал свой. */
const DEFAULT_LANGUAGE = normalizeLangCode((typeof __DEFAULT_LANG__ !== 'undefined' ? __DEFAULT_LANG__ : '').trim() || 'ru')
const FALLBACK_LANGUAGES = ['en', 'ru', 'ua', 'kz']
const LANGUAGE_ORDER = ['ru', 'en', 'ua', 'kz']
const DEFAULT_TTL_MS = 12 * 60 * 60 * 1000 // 12 hours
const HTML_LANG: Record<string, string> = {
  en: 'en',
  ru: 'ru',
  ua: 'uk',
  kz: 'kk',
}

function sortLanguages(list: string[]): string[] {
  return [...list].sort((a, b) => {
    const ai = LANGUAGE_ORDER.indexOf(a)
    const bi = LANGUAGE_ORDER.indexOf(b)
    if (ai === -1 && bi === -1) return a.localeCompare(b)
    if (ai === -1) return 1
    if (bi === -1) return -1
    return ai - bi
  })
}

/** API base URL: from current page origin at runtime; on localhost use .env (__APP_BASE_URL__). */
function getRuntimeBaseUrl(): string {
  if (typeof window !== 'undefined' && window.location?.hostname === 'localhost') {
    return (typeof __APP_BASE_URL__ !== 'undefined' && __APP_BASE_URL__) || appOrigin()
  }
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin
  }
  return (typeof __APP_BASE_URL__ !== 'undefined' && __APP_BASE_URL__) || appOrigin()
}

/** WebSocket URL: from current page host at runtime (wss://host/connection/websocket); on localhost use .env (__APP_WS_URL__). */
function getRuntimeWsUrl(): string {
  if (typeof window !== 'undefined' && window.location?.hostname === 'localhost') {
    return (typeof __APP_WS_URL__ !== 'undefined' && __APP_WS_URL__) || appWsUrl()
  }
  if (typeof window !== 'undefined' && window.location?.host) {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    return `${protocol}//ws.${window.location.host}/connection/websocket`
  }
  return (typeof __APP_WS_URL__ !== 'undefined' && __APP_WS_URL__) || appWsUrl()
}

export class I18nStore {
  language: string = DEFAULT_LANGUAGE
  availableLanguages: string[] = []
  translations: Translations = {}
  version = 0
  etag?: string
  lastFetched?: number
  ttlMs: number = DEFAULT_TTL_MS
  apiBaseUrl: string = getRuntimeBaseUrl()
  wsUrl: string = getRuntimeWsUrl()
  private initPromise: Promise<void> | null = null
  private retryTimer: number | null = null
  private retryCount = 0
  private recoveryBound = false
  private languagesPromise: Promise<void> | null = null

  constructor() {
    makeAutoObservable(this, {
      initPromise: false,
      languagesPromise: false,
    } as { initPromise: false; languagesPromise: false })
    this.hydrate()
    this.applyDocumentLang()
  }

  private applyDocumentLang() {
    if (typeof document === 'undefined') return
    document.documentElement.lang = HTML_LANG[this.language] || this.language
  }

  private persist() {
    const state: PersistedState = {
      language: this.language,
      translations: this.translations,
      etag: this.etag,
      lastFetched: this.lastFetched,
    }
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
    } catch {}
  }

  private hydrate() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      if (!raw) return
      const parsed: PersistedState = JSON.parse(raw)
      this.language = normalizeLangCode(parsed.language || DEFAULT_LANGUAGE)
      this.translations =
        parsed.translations && Object.keys(parsed.translations).length > 0
          ? parsed.translations
          : {}
      this.etag = parsed.etag
      this.lastFetched = parsed.lastFetched
    } catch {}
  }

  get isCacheFresh() {
    if (!this.lastFetched) return false
    return Date.now() - this.lastFetched < this.ttlMs
  }

  t(key: string, params?: Record<string, string | number>): string {
    let raw = this.getByPath(this.translations, key)
    if (raw && typeof raw === 'object' && !Array.isArray(raw) && params && params.count != null) {
      const form = this.pluralForm(Number(params.count))
      const picked = (raw as Record<string, unknown>)[form]
        ?? (raw as Record<string, unknown>).other
        ?? (raw as Record<string, unknown>).many
        ?? (raw as Record<string, unknown>).one
      raw = typeof picked === 'string' ? picked : undefined
    }
    const value = typeof raw === 'string' ? raw : key
    if (!params || typeof params !== 'object') return value
    return value.replace(/\{(\w+)\}/g, (_m, k) => {
      const v = params[k]
      return v !== undefined && v !== null ? String(v) : `{${k}}`
    })
  }

  private pluralForm(n: number): 'one' | 'few' | 'many' | 'other' {
    const abs = Math.abs(Math.trunc(n))
    if (this.language === 'en') return abs === 1 ? 'one' : 'other'
    const n100 = abs % 100
    const n10 = abs % 10
    if (n100 >= 11 && n100 <= 14) return 'many'
    if (n10 === 1) return 'one'
    if (n10 >= 2 && n10 <= 4) return 'few'
    return 'many'
  }

  private getByPath(obj: any, path: string): unknown {
    if (!obj || !path) return undefined
    const parts = path.split('.')
    let cur: any = obj
    for (const p of parts) {
      if (cur && typeof cur === 'object' && p in cur) {
        cur = cur[p]
      } else {
        return undefined
      }
    }
    return cur
  }

  setLanguage = async (lang: string) => {
    const next = normalizeLangCode(lang)
    if (next === this.language && Object.keys(this.translations).length > 0) return
    runInAction(() => {
      this.language = next
      this.etag = undefined
      this.lastFetched = undefined
    })
    this.applyDocumentLang()
    await this.fetchTranslations({ forceNetwork: true })
    this.persist()
  }

  init = () => {
    if (this.initPromise) return this.initPromise
    this.applyDocumentLang()
    this.bindRecovery()

    // Cached dictionary renders immediately; the network is always asked with If-None-Match.
    // Unchanged → cheap 304. New keys on the backend → fresh dict right away instead of up to
    // 12 h of raw `status.summary.all_operational` on screen.
    this.initPromise = Promise.allSettled([
      this.fetchAvailableLanguages(),
      this.fetchTranslations({ forceNetwork: true }),
    ]).then(() => undefined)

    return this.initPromise
  }

  /** A failed first load (429 from a shared IP, flaky network) must not leave raw keys until F5. */
  private scheduleRetry() {
    if (typeof window === 'undefined' || this.retryTimer != null || this.retryCount >= 3) return
    const delay = [1500, 4000, 10000][this.retryCount] ?? 10000
    this.retryCount += 1
    this.retryTimer = window.setTimeout(() => {
      this.retryTimer = null
      void this.fetchTranslations({ forceNetwork: true })
    }, delay)
  }

  private bindRecovery() {
    if (this.recoveryBound || typeof window === 'undefined') return
    this.recoveryBound = true
    const refetchIfEmpty = () => {
      if (Object.keys(this.translations).length === 0 && this.retryTimer == null) {
        this.retryCount = 0
        void this.fetchTranslations({ forceNetwork: true })
      }
    }
    window.addEventListener('online', refetchIfEmpty)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') refetchIfEmpty()
    })
  }

  private buildHeaders(): HeadersInit {
    return {
      'Accept-Language': this.language,
      'x-lang': this.language,
      ...(this.etag ? { 'If-None-Match': this.etag } : {}),
    }
  }

  private fetchAvailableLanguages() {
    if (this.availableLanguages.length > 0) return Promise.resolve()
    if (this.languagesPromise) return this.languagesPromise
    this.languagesPromise = this.loadAvailableLanguages().finally(() => {
      if (this.availableLanguages.length === 0) this.languagesPromise = null
    })
    return this.languagesPromise
  }

  private async loadAvailableLanguages() {
    try {
      const res = await fetch(this.joinUrl('/api/i18n/list'), {
        method: 'GET',
        headers: this.buildHeaders(),
      })
      if (!res.ok) return
      const list: string[] = await res.json().then(data => data.languages)
      const normalized = Array.isArray(list)
        ? list.map((code) => normalizeLangCode(String(code))).filter(Boolean)
        : []
      runInAction(() => {
        this.availableLanguages =
          normalized.length > 0 ? sortLanguages(normalized) : FALLBACK_LANGUAGES
      })
    } catch {
      runInAction(() => {
        if (this.availableLanguages.length === 0) this.availableLanguages = FALLBACK_LANGUAGES
      })
    }
  }

  private async fetchTranslations(options?: { forceNetwork?: boolean }) {
    const { forceNetwork } = options || {}

    if (!forceNetwork && this.isCacheFresh && Object.keys(this.translations).length > 0) {
      return
    }

    try {
      const res = await fetch(this.joinUrl(`/api/i18n/${this.language}`), {
        method: 'GET',
        cache: forceNetwork ? 'no-cache' : 'default',
        headers: this.buildHeaders(),
      })

      if (res.status === 304) {
        runInAction(() => {
          this.lastFetched = Date.now()
        })
        this.persist()
        return
      }

      if (!res.ok) {
        this.scheduleRetry()
        return
      }

      const etagHeader = res.headers.get('ETag') || undefined
      const dict = (await res.json().then(data => data.dict)) as Translations
      runInAction(() => {
        if (dict && Object.keys(dict).length > 0) {
          this.translations = dict
          this.version += 1
        }
        this.etag = etagHeader
        this.lastFetched = Date.now()
      })
      this.retryCount = 0
      this.applyDocumentLang()
      this.persist()
    } catch {
      // network errors: keep existing cache, try again shortly
      this.scheduleRetry()
    }
  }

  private joinUrl(path: string): string {
    const base = this.apiBaseUrl?.replace(/\/$/, '') || ''
    const suffix = path.startsWith('/') ? path : `/${path}`
    return `${base}${suffix}`
  }
}

export const i18nStore = new I18nStore()

export function t(
  key: string,
  params?: Record<string, string | number>,
): string {
  return i18nStore.t(key, params)
}

